import React, { useEffect, useState } from 'react';
import { addComment, loadComments, type CommentItem } from '../lib/comments';
import { type AuthUser, getCachedAuthUser, authStatus } from '../lib/auth';

export type CommentSectionProps = {
  slug?: string;
};

const CommentSection: React.FC<CommentSectionProps> = ({ slug }) => {
  const [items, setItems] = useState<CommentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [authUser, setAuthUser] = useState<AuthUser | null>(() => getCachedAuthUser());
  const [statusMsg, setStatusMsg] = useState<string>('');
  const [cooldownUntil, setCooldownUntil] = useState<number>(0);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const st = await authStatus();
        if (!active) return;
        if (st.ok && st.user) setAuthUser(st.user as AuthUser);
      } catch {}
    })();
    return () => { active = false; };
  }, []);

  // Prefill author details from auth
  useEffect(() => {
    if (!authUser) return;
    const displayName = authUser.name || authUser.username || '';
    const displayEmail = authUser.email || '';
    setName(displayName);
    if (displayEmail) setEmail(displayEmail);
  }, [authUser]);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!slug) { setLoading(false); return; }
      setLoading(true);
      try { const list = await loadComments(slug); if (active) setItems(list); }
      finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [slug]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!slug || !name || !message) return;
    if (Date.now() < cooldownUntil) return;
    setSubmitting(true);
    try {
      const res = await addComment(slug, { name, email, message });
      if (res.ok && res.comment) {
        setItems(prev => [...prev, res.comment!]);
        setName(''); setEmail(''); setMessage('');
        setStatusMsg('Comment posted');
        setCooldownUntil(Date.now() + 3000);
        setTimeout(()=> setStatusMsg(''), 2000);
      } else if ((res as any).duplicate) {
        // do not append duplicate; optionally surface message
        setStatusMsg('Duplicate comment ignored');
        setTimeout(()=> setStatusMsg(''), 2000);
      } else if (res.error) {
        setStatusMsg(res.error || 'Failed to post');
        setTimeout(()=> setStatusMsg(''), 2500);
      }
    } finally { setSubmitting(false); }
  }

  // Build a simple tree (2 levels for readability)
  const roots = items.filter(c => !c.parentId);
  const childrenByParent: Record<string, CommentItem[]> = {};
  for (const c of items) {
    if (!c.parentId) continue;
    (childrenByParent[c.parentId] ||= []).push(c);
  }

  const [replyOpen, setReplyOpen] = useState<Record<string, boolean>>({});
  const [replyDrafts, setReplyDrafts] = useState<Record<string, string>>({});

  async function submitReply(parentId: string) {
    if (!slug) return;
    const draft = (replyDrafts[parentId] || '').trim();
    if (!draft) return;
    if (Date.now() < cooldownUntil) return;
    setSubmitting(true);
    try {
      const res = await addComment(slug, { name, email, message: draft, parentId });
      if (res.ok && res.comment) {
        setItems(prev => [...prev, res.comment!]);
        setReplyDrafts(d => ({ ...d, [parentId]: '' }));
        setReplyOpen(o => ({ ...o, [parentId]: false }));
        setStatusMsg('Reply posted');
        setCooldownUntil(Date.now() + 3000);
        setTimeout(()=> setStatusMsg(''), 2000);
      } else if ((res as any).duplicate) {
        setStatusMsg('Duplicate reply ignored');
        setTimeout(()=> setStatusMsg(''), 2000);
      } else if (res.error) {
        setStatusMsg(res.error || 'Failed to post');
        setTimeout(()=> setStatusMsg(''), 2500);
      }
    } finally { setSubmitting(false); }
  }

  function renderNode(c: CommentItem, depth: number): React.ReactElement {
    const kids = childrenByParent[c.id] || [];
    return (
      <li key={c.id} className={`${depth === 0 ? 'rounded-xl p-3 bg-gray-50' : 'rounded-lg p-2.5 bg-white'} border border-gray-200 ${depth>0 ? 'ml-6' : ''} `}>
        <div className="flex items-start gap-3">
          <span className={`${depth===0 ? 'h-8 w-8' : 'h-7 w-7'} inline-flex items-center justify-center rounded-full bg-gradient-to-br from-purple-600 to-blue-600 text-white ${depth===0 ? 'text-xs' : 'text-[10px]'} font-semibold uppercase`}>
            {(c.name || '?').slice(0,1)}
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <div className={`${depth===0 ? 'text-sm' : 'text-xs'} font-medium text-gray-900 truncate`}>{c.name}</div>
              <div className={`${depth===0 ? 'text-xs' : 'text-[11px]'} text-gray-500`}>{new Date(c.createdAt).toLocaleString()}</div>
            </div>
            {/* Parent mention for replies */}
            {c.parentId && childrenByParent && (
              <div className="mt-1 inline-flex items-center gap-1 rounded-full border border-purple-100 bg-purple-50 text-purple-700 px-2.5 py-0.5 text-xs font-medium">
                <span>Replying to</span>
                <span className="font-semibold">@{(items.find(it=>it.id===c.parentId)?.name || 'comment')}</span>
              </div>
            )}
            <div className="text-sm text-gray-800 whitespace-pre-wrap mt-1">{c.message}</div>

            {kids.length > 0 && (
              <ul className="mt-3 space-y-2 pl-4 border-l-2 border-purple-100">
                {kids.map(k => renderNode(k, depth+1))}
              </ul>
            )}

            <div className="mt-3">
              <button
                type="button"
                onClick={() => setReplyOpen(o => ({ ...o, [c.id]: !o[c.id] }))}
                className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-3 py-1 text-xs text-gray-700 hover:bg-gray-50"
              >
                <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 19l-7-7 7-7"/><path d="M3 12h12a6 6 0 0 1 6 6v1"/></svg>
                Reply
              </button>
              {replyOpen[c.id] && (
                <div className="mt-2">
                  <textarea
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-purple-500 min-h-[70px]"
                    value={replyDrafts[c.id] || ''}
                    onChange={(e)=> setReplyDrafts(d => ({ ...d, [c.id]: e.target.value }))}
                    placeholder="Write a reply…"
                  />
                  <div className="mt-2 flex items-center justify-end gap-2">
                    <button
                      type="button"
                      className="inline-flex items-center rounded-md border border-gray-200 px-3 py-1.5 text-xs hover:bg-gray-50"
                      onClick={() => setReplyOpen(o => ({ ...o, [c.id]: false }))}
                    >Cancel</button>
                    <button
                      type="button"
                      disabled={!slug || submitting || !(replyDrafts[c.id]||'').trim()}
                      onClick={() => submitReply(c.id)}
                      className="inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-60"
                    >
                      {submitting ? <span className="h-4 w-4 rounded-full border-2 border-white/40 border-t-white animate-spin" /> : null}
                      Reply
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </li>
    );
  }

  return (
    <section className="mt-10">
      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="p-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900">Comments</h3>
          <p className="text-sm text-gray-600">Share your thoughts. Be kind and constructive.</p>
          {!slug && (
            <div className="mt-2 inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 text-amber-800 px-3 py-1 text-xs font-medium">
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 9v4"/><path d="M12 17h.01"/><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/></svg>
              Preview only — save and set a URL slug to enable comments.
            </div>
          )}
          <div className="mt-2 inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-gray-700">
            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-gradient-to-r from-purple-500 to-blue-500 text-white text-[10px] font-semibold">
              {(authUser?.username || 'G').slice(0,1).toUpperCase()}
            </span>
            {authUser ? (
              <span>Signed in as <strong className="font-semibold text-gray-900">{authUser.name || authUser.username}</strong></span>
            ) : (
              <span>Not signed in — you can still comment by entering your name</span>
            )}
          </div>
          {statusMsg && (
            <div className="mt-2 inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 text-blue-800 px-3 py-1 text-xs font-medium">
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10a8 8 0 10-16 0c0 6 8 10 8 10z"/><circle cx="12" cy="12" r="3"/></svg>
              {statusMsg}
            </div>
          )}
        </div>

        <div className="p-4">
          {loading ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span className="relative inline-flex h-4 w-4">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-60 animate-ping" />
                  <span className="relative inline-flex rounded-full h-4 w-4 bg-gradient-to-br from-purple-600 to-blue-600 animate-spin [animation-duration:1400ms]" />
                </span>
                Fetching comments…
              </div>
              <ul className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <li key={i} className="rounded-xl border border-gray-200 p-3 bg-gray-50">
                    <div className="flex items-start gap-3 animate-pulse">
                      <div className="h-8 w-8 rounded-full bg-purple-200/60" />
                      <div className="flex-1 min-w-0 space-y-2">
                        <div className="h-3 w-32 bg-gray-200 rounded" />
                        <div className="h-3 w-24 bg-gray-200 rounded" />
                        <div className="h-3 w-full bg-gray-200 rounded" />
                        <div className="h-3 w-5/6 bg-gray-200 rounded" />
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ) : items.length === 0 ? (
            <div className="text-sm text-gray-600">No comments yet. Be the first to comment!</div>
          ) : (
            <ul className="space-y-3">
              {roots.map((c) => renderNode(c, 0))}
            </ul>
          )}
        </div>

        <div className="p-4 border-t border-gray-100">
          <form onSubmit={onSubmit} className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="text-xs text-gray-600">Name</label>
              <input className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-50" value={name} onChange={e=>setName(e.target.value)} placeholder="Your name" disabled={!!authUser} />
            </div>
            {!authUser && (
              <div>
                <label className="text-xs text-gray-600">Email (optional)</label>
                <input className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-purple-500" value={email} onChange={e=>setEmail(e.target.value)} placeholder="you@example.com" />
              </div>
            )}
            <div className="sm:col-span-2">
              <label className="text-xs text-gray-600">Message</label>
              <textarea className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-purple-500 min-h-[90px]" value={message} onChange={e=>setMessage(e.target.value)} placeholder="Write your comment..." />
            </div>
            <div className="sm:col-span-2 flex justify-end">
              <button type="submit" disabled={!slug || submitting || !name || !message} className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-60">
                {submitting ? <span className="h-4 w-4 rounded-full border-2 border-white/40 border-t-white animate-spin" /> : null}
                {submitting ? 'Posting…' : (!slug ? 'Save post to enable' : 'Post comment')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default CommentSection;
